import type { LatLong } from '@iheartjane/geolocation';
import type { StoreType } from '@iheartjane/types';
import { Storage } from '@iheartjane/utils';
import { isbot } from 'isbot';
import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { DEFAULT_LOCATION_COORDINATES } from '@jane/search/util';
import type {
  StoreFulfillmentTypes,
  UserLocation,
  UserPreferences,
} from '@jane/shared/types';

const getInitialLocation = (withBotLocation = false): UserLocation => {
  if (withBotLocation && isbot(navigator.userAgent)) {
    return { ...DEFAULT_LOCATION_COORDINATES, hasResetLocation: false };
  }

  return {
    cityState: Storage.get<string>('cityState'),
    coordinates: Storage.get<LatLong>('coordinates'),
    countryCode: Storage.get<string>('countryCode'),
    hasResetLocation: false,
    street: Storage.get<string>('street'),
    zipcode: Storage.get<string>('zipcode'),
  };
};

const getInitialPreferences = (): UserPreferences => ({
  storeAvailability: Storage.get<'openNow' | undefined>('storeAvailability'),
  storeFulfillmentType: Storage.get<StoreFulfillmentTypes>(
    'storeFulfillmentType'
  ),
  storeSearchRadius: Storage.get<number>('storeSearchRadius'),
  storeType: Storage.get<StoreType>('storeType'),
});

interface UserPreferencesContext {
  newLocation?: boolean;
  resetLocation: () => void;
  resetPreferences: () => void;
  setNewLocation: (next: boolean) => void;
  setUserLocation: (location: UserLocation) => void;
  setUserPreferences: (preferences: UserPreferences) => void;
  userLocation: UserLocation;
  userPreferences: UserPreferences;
}

export const UserPreferencesContext = createContext<UserPreferencesContext>({
  newLocation: false,
  resetLocation: () => null,
  resetPreferences: () => null,
  setNewLocation: () => null,
  setUserLocation: () => null,
  setUserPreferences: () => null,
  userLocation: getInitialLocation(),
  userPreferences: getInitialPreferences(),
});
UserPreferencesContext.displayName = 'UserPreferences';

export const useUserPreferences = () => useContext(UserPreferencesContext);

export const UserPreferencesProvider = ({
  children,
  withBotLocation = false,
}: PropsWithChildren<{ withBotLocation?: boolean }>) => {
  const [userLocation, setUserLocation] = useState(() =>
    getInitialLocation(withBotLocation)
  );
  const [userPreferences, setUserPreferences] = useState(
    getInitialPreferences()
  );
  const [newLocation, setNewLocation] = useState(false);

  const handleSetPreferences = useCallback((preferences: UserPreferences) => {
    Object.keys(preferences).forEach((key) =>
      Storage.set(key, preferences[key as keyof UserPreferences])
    );
    setUserPreferences(preferences);
  }, []);

  const handleSetLocation = useCallback((location: UserLocation) => {
    Object.keys(location).forEach((key) =>
      Storage.set(key, location[key as keyof UserLocation])
    );
    setUserLocation(location);
    setNewLocation(true);
  }, []);

  const handleResetLocation = useCallback(() => {
    const locationKeys = [
      'coordinates',
      'cityState',
      'countryCode',
      'street',
      'zipcode',
    ];
    locationKeys.forEach((key) => {
      Storage.remove(key);
    });

    setUserLocation(
      locationKeys.reduce((prev, curr) => ({ ...prev, [curr]: undefined }), {
        hasResetLocation: true,
      })
    );
  }, []);

  const handleResetPreferences = useCallback(() => {
    const preferencesKeys = [
      'storeAvailability',
      'storeFulfillmentType',
      'storeSearchRadius',
      'storeType',
    ];
    preferencesKeys.forEach((key) => {
      Storage.remove(key);
    });

    setUserPreferences(
      preferencesKeys.reduce(
        (prev, curr) => ({ ...prev, [curr]: undefined }),
        {}
      )
    );
  }, []);

  const value = useMemo(
    () => ({
      newLocation,
      resetLocation: handleResetLocation,
      setNewLocation,
      resetPreferences: handleResetPreferences,
      setUserLocation: handleSetLocation,
      setUserPreferences: handleSetPreferences,
      userLocation,
      userPreferences,
    }),
    [
      handleResetLocation,
      handleResetPreferences,
      handleSetLocation,
      handleSetPreferences,
      newLocation,
      setNewLocation,
      userLocation,
      userPreferences,
    ]
  );

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};
